import * as React from "react";
import { useEffect, useState } from "react";
import { Link as RouterLink, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  ButtonBase,
  Chip,
  Container,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon, ZoomIn as ZoomInIcon } from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Navbar from '../../components/navbar.jsx';
import { WasPrice, formatRupees } from '../../components/billzappPlans.jsx';
import BillingMachineOrderDialog from '../../components/billingMachineOrderDialog.jsx';
import SiteFooter from '../../components/siteFooter.jsx';
import { BILLZAPP_BUNDLES, BILLZAPP_PLANS, BILLZAPP_PRINTERS } from '../../data/billzappPlans';

const defaultTheme = createTheme();

const findBundle = (printerId, planId) =>
  BILLZAPP_BUNDLES.find((bundle) => bundle.printer.id === printerId && bundle.plan.id === planId);

// Where the photos sit in the gallery. Picking a printer shows its photo; picking software
// shows its poster.
const PRINTER_PHOTO = 0;
const POSTER_PHOTO = 1;

// Printer / software switch: picks one of the four bundles and moves to its page.
function OptionPicker({ label, value, options, onChange }) {
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        fullWidth
        color="primary"
        onChange={(event, picked) => picked && onChange(picked)}
      >
        {options.map((option) => (
          <ToggleButton key={option.value} value={option.value} sx={{ textTransform: 'none', flexDirection: 'column', py: 1 }}>
            <Typography fontWeight="bold">{option.name}</Typography>
            <Typography variant="caption" color="text.secondary">{option.price}</Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

// The bundle's photos: the printer, then its software's features poster. The thumbnails under
// the big photo switch between them, and clicking the big photo opens it full size (the
// poster's feature list is small on a phone).
function PhotoGallery({ photos, index, onChange }) {
  const [zoomed, setZoomed] = useState(false);
  const photo = photos[index];
  return (
    <Box>
      <ButtonBase
        onClick={() => setZoomed(true)}
        aria-label={`View the ${photo.label} full size`}
        sx={{
          display: 'block',
          width: '100%',
          position: 'relative',
          bgcolor: '#f1f5f9',
          borderRadius: 3,
          p: { xs: 3, md: 5 },
          cursor: 'zoom-in',
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }
        }}
      >
        <Box
          component="img"
          src={photo.src}
          alt={photo.alt}
          sx={{
            height: { xs: 300, md: 440 },
            maxWidth: '100%',
            objectFit: 'contain',
            display: 'block',
            mx: 'auto',
            // The photos have white backgrounds; this blends them into the grey panel.
            mixBlendMode: 'multiply'
          }}
        />
        <ZoomInIcon sx={{ position: 'absolute', right: 12, bottom: 12, color: 'text.secondary' }} />
      </ButtonBase>
      <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
        {photos.map((thumb, thumbIndex) => (
          <ButtonBase
            key={thumb.src}
            onClick={() => onChange(thumbIndex)}
            aria-label={`Show the ${thumb.label}`}
            aria-pressed={thumbIndex === index}
            sx={{
              width: 72,
              height: 88,
              p: 0.75,
              borderRadius: 2,
              bgcolor: '#f1f5f9',
              border: '2px solid',
              borderColor: thumbIndex === index ? 'primary.main' : 'transparent',
              '&:hover': { borderColor: thumbIndex === index ? 'primary.main' : 'grey.400' },
              '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }
            }}
          >
            <Box
              component="img"
              src={thumb.src}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'contain', mixBlendMode: 'multiply' }}
            />
          </ButtonBase>
        ))}
      </Stack>

      <Dialog
        open={zoomed}
        onClose={() => setZoomed(false)}
        maxWidth={false}
        aria-label={photo.alt}
        PaperProps={{ sx: { m: 1, maxWidth: 'calc(100% - 16px)', maxHeight: 'calc(100% - 16px)' } }}
      >
        <IconButton
          onClick={() => setZoomed(false)}
          aria-label="close"
          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255, 255, 255, 0.85)', '&:hover': { bgcolor: 'white' } }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          component="img"
          src={photo.src}
          alt={photo.alt}
          sx={{ display: 'block', maxWidth: '100%', maxHeight: 'calc(100vh - 16px)', objectFit: 'contain' }}
        />
      </Dialog>
    </Box>
  );
}

// The page for one "Pocket Billing Machine With Software" bundle, opened from its card on
// /products. "Order Now" takes the order and payment in BillingMachineOrderDialog.
export default function ProductDetailPage() {
  const { bundleId } = useParams();
  const navigate = useNavigate();
  const [ordering, setOrdering] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  // The cards sit partway down /products; start this page at the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const bundle = BILLZAPP_BUNDLES.find((candidate) => candidate.slug === bundleId);
  if (!bundle) return <Navigate to="/products" replace />;

  const { printer, plan, software } = bundle;
  const planPaise = plan.annual.amountPaise;
  const totalPaise = printer.amountPaise + planPaise;
  // On sale like the plan cards: the software year's "was" price, and the total with it.
  const planWasPaise = plan.annual.wasAmountPaise;

  // Switching printer or software replaces this page rather than stacking up history.
  const showBundle = (printerId, planId) =>
    navigate(`/products/${findBundle(printerId, planId).slug}`, { replace: true });

  const photos = [
    {
      src: printer.image,
      label: "printer photo",
      alt: `BillZapp app on a phone next to the BillZapp ${printer.name} receipt printer`
    },
    {
      src: plan.poster,
      label: `${software} features poster`,
      alt: `BillZapp ${software} on a phone, with a list of the software's features`
    }
  ];

  return (
    <ThemeProvider theme={defaultTheme}>
      <CssBaseline />
      <Navbar />

      {/* Product */}
      <Box sx={{ pt: { xs: 12, md: 15 }, pb: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Breadcrumbs sx={{ mb: { xs: 3, md: 5 } }}>
            <Link component={RouterLink} to="/products" underline="hover" color="inherit">
              Products
            </Link>
            <Typography color="text.primary">{bundle.title}</Typography>
          </Breadcrumbs>

          <Grid container spacing={{ xs: 4, md: 6 }}>
            <Grid item xs={12} md={6}>
              <PhotoGallery photos={photos} index={photoIndex} onChange={setPhotoIndex} />
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <Box>
                  {plan.recommended && (
                    <Chip label="RECOMMENDED" color="primary" size="small" sx={{ fontWeight: 'bold', mb: 1.5 }} />
                  )}
                  <Typography
                    variant="h1"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, lineHeight: 1.2, mb: 1.5 }}
                  >
                    {bundle.title}
                  </Typography>
                  <Typography color="text.secondary">
                    A BillZapp {printer.name} receipt printer and a year of BillZapp {plan.name}, so you
                    can start billing on day one. Pay for the printer once, then renew the software every year.
                  </Typography>
                </Box>

                <Box>
                  <Chip
                    label={`ON SALE · SAVE ${formatRupees(planWasPaise - planPaise)}`}
                    color="error"
                    size="small"
                    sx={{ fontWeight: 'bold', mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                    <WasPrice paise={printer.amountPaise + planWasPaise} variant="h5" sx={{ fontWeight: 500 }} />
                    <Typography variant="h3" component="p" fontWeight="bold">
                      {formatRupees(totalPaise)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Printer + 1 year of software
                  </Typography>
                </Box>

                <OptionPicker
                  label="Printer"
                  value={printer.id}
                  options={BILLZAPP_PRINTERS.map((option) => ({
                    value: option.id,
                    name: option.name,
                    price: formatRupees(option.amountPaise)
                  }))}
                  onChange={(printerId) => {
                    setPhotoIndex(PRINTER_PHOTO);
                    showBundle(printerId, plan.id);
                  }}
                />
                <OptionPicker
                  label="Software"
                  value={plan.id}
                  options={BILLZAPP_PLANS.map((option) => ({
                    value: option.id,
                    name: option.name.replace(/ Plan$/, ""),
                    price: (
                      <>
                        <WasPrice paise={option.annual.wasAmountPaise} variant="caption" />{" "}
                        {formatRupees(option.annual.amountPaise)}/yr
                      </>
                    )
                  }))}
                  onChange={(planId) => {
                    setPhotoIndex(POSTER_PHOTO);
                    showBundle(printer.id, planId);
                  }}
                />

                <Box>
                  <Divider />
                  <Stack spacing={1} sx={{ my: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckIcon sx={{ color: 'success.main', fontSize: 20, mt: 0.25 }} />
                      <Typography sx={{ flex: 1 }}>BillZapp {printer.name} receipt printer</Typography>
                      <Typography fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                        {formatRupees(printer.amountPaise)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckIcon sx={{ color: 'success.main', fontSize: 20, mt: 0.25 }} />
                      <Typography sx={{ flex: 1 }}>BillZapp {plan.name} (1 year)</Typography>
                      <WasPrice paise={planWasPaise} />
                      <Typography fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                        {formatRupees(planPaise)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Divider />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    One-time printer cost. Software renews at {formatRupees(planPaise)} a year.
                  </Typography>
                </Box>

                <Box>
                  <Button variant="contained" size="large" fullWidth onClick={() => setOrdering(true)}>
                    Order Now
                  </Button>
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1.5 }}>
                    Pay securely online through Razorpay, or order by phone on +91 98224 12353.
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Software features */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" fontWeight="bold" sx={{ mb: 1, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
            What BillZapp {software} includes
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            Your first year of {plan.name} comes with the printer.
          </Typography>
          <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={2}>
              {plan.features.map((feature) => (
                <Grid item xs={12} sm={6} key={feature}>
                  <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckIcon sx={{ color: 'success.main', mr: 1 }} />
                    {feature}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
          <Link href="/billzapp" variant="body1" sx={{ display: 'inline-block', mt: 3 }}>
            See all BillZapp features
          </Link>
          <Box textAlign="center" sx={{ mt: 6 }}>
            <Button variant="contained" size="large" onClick={() => setOrdering(true)} sx={{ px: 6 }}>
              Order Now for {formatRupees(totalPaise)}
            </Button>
          </Box>
        </Container>
      </Box>

      {ordering && (
        <BillingMachineOrderDialog
          key={bundle.slug}
          plan={plan}
          printer={printer}
          title={bundle.title}
          onClose={() => setOrdering(false)}
        />
      )}

      <SiteFooter />
    </ThemeProvider>
  );
}
